import { useState } from 'react';

import { t } from '@lingui/macro';
import { useUpdateEffect } from 'usehooks-ts';

import { CLIENT_SELECTOR_FIELDNAME } from '../components/business/checkout/CheckoutClientSelect';
import { CHECKOUT_EXTRA_DATA_FIELDNAME } from '../components/business/checkout/form/CheckoutFieldsExtraData';
import { PHONE_COUNTRYCODE_FIELDNAME, PHONE_FIELDNAME } from '../components/generic/form/InputPhone';
import { apiGetClientPreviousOrder, ROUTE_PAGE_CHECKOUT_SUCCESS } from '../constants';
import { findArrayItemByKey } from '../utils/arrayUtil';
import useConfirmStripePayment from './useConfirmStripePayment';
import useFetchClientPreviousOrder from './useFetchClientPreviousOrder';
import useFormValidate from './useFormValidate';
import useNavigateBookingEngine from './useNavigateBookingEngine';
import usePostCheckout from './usePostCheckout';
import useReadDefaultPaymentData, { updatePaymentDataWithSelectedClient } from './useReadDefaultPaymentData';
import useReadPaymentMethods from './useReadPaymentMethods';

const useFormCheckout = (cartMainClientId, setUnavailableItems, clientSelectorChoices, areChoicesStaffs, currency, totalPrice, stripeRef) => {
  const { isInvalidForm } = useFormValidate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOrderCreated, setIsOrderCreated] = useState(false);
  const [stripeErrorMessage, setStripeErrorMessage] = useState(null);

  const paymentMethods = useReadPaymentMethods();

  const defaultPaymentData = useReadDefaultPaymentData();
  const [paymentData, setPaymentData] = useState({ ...defaultPaymentData });

  const navigate = useNavigateBookingEngine();
  const confirmStripePayment = useConfirmStripePayment(stripeRef);

  const { data, status, writeCheckout } = usePostCheckout(cartMainClientId, currency, totalPrice, clientSecret =>
    handleStripeOrderCreated(clientSecret)
  );

  const { setFetchOrderUrl, previousOrderDetails } = useFetchClientPreviousOrder();

  const handleOnChange = (fieldName, newValue, additionalData, selectLabel) => {
    if (fieldName === PHONE_FIELDNAME) {
      return handleChangePhone(newValue, additionalData);
    } else if (fieldName === CLIENT_SELECTOR_FIELDNAME) {
      return handleChangeClientSelector(newValue);
    } else if (fieldName === CHECKOUT_EXTRA_DATA_FIELDNAME) {
      return handleChangeExtraData(newValue, additionalData, selectLabel);
    }

    // standard cases
    setPaymentData({ ...paymentData, [fieldName]: newValue });
  };

  const handleChangePhone = (phoneValue, phoneCountryCode) => {
    setPaymentData({ ...paymentData, [PHONE_FIELDNAME]: phoneValue, [PHONE_COUNTRYCODE_FIELDNAME]: phoneCountryCode });
  };

  const handleChangeExtraData = (extraDataValue, extraDataInternalLabel, selectLabel) => {
    const currentExtraData = { ...paymentData[CHECKOUT_EXTRA_DATA_FIELDNAME] };
    currentExtraData[extraDataInternalLabel] = selectLabel != null ? selectLabel : extraDataValue;
    setPaymentData({ ...paymentData, [CHECKOUT_EXTRA_DATA_FIELDNAME]: currentExtraData });
  };

  const handleChangeClientSelector = newValue => {
    // When selecting a client, we will update several fields (or reset if client was unselected)
    const selectedClient = newValue ? findArrayItemByKey('value', newValue, clientSelectorChoices) : null;
    if (!selectedClient) {
      setFetchOrderUrl(null);
      setPaymentData({ ...defaultPaymentData });
      return;
    }

    const newPaymentData = updatePaymentDataWithSelectedClient(paymentData, selectedClient, defaultPaymentData, areChoicesStaffs);
    setFetchOrderUrl(apiGetClientPreviousOrder(selectedClient.id));
    setPaymentData(newPaymentData);
  };

  const dataProps = {
    onChange: handleOnChange,
    isRequired: true,
  };

  // The order exists before the payment, a declined card must not create a second one
  const handleStripeOrderCreated = clientSecret => {
    setIsOrderCreated(true);

    if (clientSecret !== stripeRef.current.clientSecret) {
      console.error('Stripe client secret mismatch between get-checkout-data and checkout');
      handleStripeFailure(t`An error occured, please try again later.`);
      return;
    }

    handleStripeConfirmation();
  };

  const handleStripeConfirmation = async () => {
    const { errorMessage } = await confirmStripePayment(paymentData);
    if (errorMessage) {
      handleStripeFailure(errorMessage);
      return;
    }

    navigate(ROUTE_PAGE_CHECKOUT_SUCCESS);
  };

  const handleStripeFailure = errorMessage => {
    setStripeErrorMessage(errorMessage);
    setIsSubmitting(false);
  };

  const handleSubmit = e => {
    e.preventDefault();
    setStripeErrorMessage(null);

    if (isInvalidForm(e.nativeEvent.target)) {
      return;
    }

    // Stripe fields live in their own iframes so our form validation cannot see them
    if (stripeRef.current.isRequired && !stripeRef.current.isCardComplete) {
      setStripeErrorMessage(t`Please fill in your card details.`);
      return;
    }

    setIsSubmitting(true);

    if (isOrderCreated) {
      handleStripeConfirmation();
      return;
    }

    writeCheckout(paymentData);
  };

  useUpdateEffect(() => {
    if (data && data.unavailable_items) {
      setUnavailableItems(data.unavailable_items);
      setIsSubmitting(false);
    }
  }, [data]);

  return {
    handleSubmit,
    paymentData,
    dataProps,
    isSubmitting,
    status,
    data,
    paymentMethods,
    previousOrderDetails,
    isOrderCreated,
    stripeErrorMessage,
  };
};

export default useFormCheckout;
